import type { SecurityPrincipal, SecuritySchema } from '../../../types/security';
import type { ScriptOperation, SecurityExplorerNodeContext } from './securityExplorerNodeContext';

export type SecurityApi = NonNullable<Window['electronAPI']>['securityManagement'];

function getRoleOwner(principal: SecurityPrincipal | undefined): string | undefined {
  return principal?.engine === 'mssql' ? principal.mssqlDetails?.ownerName : undefined;
}

function getSchemaOwner(schema: SecuritySchema | undefined): string | undefined {
  return schema?.ownerName;
}

function getLoginPasswordPlaceholder(principal: SecurityPrincipal | undefined): string | undefined {
  if (principal?.engine !== 'mssql') return undefined;
  return principal.mssqlDetails?.loginType === 'SQL_LOGIN' ? 'REPLACE_WITH_STRONG_PASSWORD' : undefined;
}

async function previewLoginScript(options: {
  api: SecurityApi;
  context: SecurityExplorerNodeContext;
  operation: ScriptOperation;
  notes: string[];
}) {
  const { api, context, operation, notes } = options;
  const loginsResult = await api.fetchLogins({ connectionId: context.connectionId, database: context.database });
  const principal = loginsResult.success ? (loginsResult.logins ?? []).find((l) => l.name === context.principalName) : undefined;

  const password = operation === 'create' ? getLoginPasswordPlaceholder(principal) : undefined;
  if (password) notes.push('Passwords are not retrievable; replace REPLACE_WITH_STRONG_PASSWORD before executing.');

  return api.previewScript({
    connectionId: context.connectionId,
    operation,
    target: 'login',
    params: {
      name: context.principalName,
      authType: principal?.mssqlDetails?.loginType,
      password,
      defaultDatabase: principal?.mssqlDetails?.defaultDatabase,
      checkPolicy: principal?.mssqlDetails?.checkPolicy,
      checkExpiration: principal?.mssqlDetails?.checkExpiration,
      isDisabled: operation === 'alter' ? principal?.mssqlDetails?.isDisabled : undefined,
    } as any,
  });
}

async function previewUserScript(options: {
  api: SecurityApi;
  context: SecurityExplorerNodeContext;
  operation: ScriptOperation;
}) {
  const { api, context, operation } = options;
  if (!context.database) throw new Error('Database is required for scripting a database user');

  const usersResult = await api.fetchUsers({ connectionId: context.connectionId, database: context.database });
  const principal = usersResult.success ? (usersResult.users ?? []).find((u) => u.name === context.principalName) : undefined;
  const isExternalUser =
    principal?.engine === 'mssql' &&
    (principal.mssqlDetails?.userType === 'EXTERNAL_USER' || principal.mssqlDetails?.userType === 'EXTERNAL_GROUP');

  return api.previewScript({
    connectionId: context.connectionId,
    database: context.database,
    operation,
    target: 'user',
    params: {
      name: context.principalName,
      forLogin: operation === 'create' && !isExternalUser ? principal?.mssqlDetails?.mappedLoginName : undefined,
      fromExternalProvider: operation === 'create' && isExternalUser ? true : undefined,
      withoutLogin: operation === 'create' && !isExternalUser ? !principal?.mssqlDetails?.mappedLoginName : undefined,
      defaultSchema: principal?.mssqlDetails?.defaultSchema,
      withLogin: operation === 'alter' ? principal?.mssqlDetails?.mappedLoginName : undefined,
    } as any,
  });
}

async function previewSchemaScript(options: {
  api: SecurityApi;
  context: SecurityExplorerNodeContext;
  operation: ScriptOperation;
}) {
  const { api, context, operation } = options;
  if (!context.database) throw new Error('Database is required for scripting a schema');

  const schemasResult = await api.fetchSchemas({ connectionId: context.connectionId, database: context.database });
  const schema = schemasResult.success ? (schemasResult.schemas ?? []).find((s) => s.name === context.principalName) : undefined;

  return api.previewScript({
    connectionId: context.connectionId,
    database: context.database,
    operation,
    target: 'schema',
    params: {
      name: context.principalName,
      owner: operation === 'create' ? getSchemaOwner(schema) : undefined,
      newOwner: operation === 'alter' ? getSchemaOwner(schema) : undefined,
    } as any,
  });
}

async function previewRoleScript(options: {
  api: SecurityApi;
  context: SecurityExplorerNodeContext;
  operation: ScriptOperation;
}) {
  const { api, context, operation } = options;
  const isDatabaseRole = context.nodeType === 'database-role';
  const isServerRole = context.nodeType === 'server-role';

  if (isDatabaseRole && !context.database) throw new Error('Database is required for scripting a database role');

  let principal: SecurityPrincipal | undefined;
  if (isDatabaseRole) {
    const rolesResult = await api.fetchDatabaseRoles({
      connectionId: context.connectionId,
      database: context.database as string,
    });
    principal = rolesResult.success ? (rolesResult.roles ?? []).find((r) => r.name === context.principalName) : undefined;
  } else {
    const rolesResult = await api.fetchServerRoles({ connectionId: context.connectionId });
    principal = rolesResult.success ? (rolesResult.roles ?? []).find((r) => r.name === context.principalName) : undefined;
  }

  const result = await api.previewScript({
    connectionId: context.connectionId,
    database: isDatabaseRole ? context.database : undefined,
    operation,
    target: 'role',
    params: {
      name: context.principalName,
      owner: operation === 'create' ? getRoleOwner(principal) : undefined,
      isServerRole: isServerRole || undefined,
      canLogin: principal?.engine === 'pgsql' ? principal.pgsqlDetails?.canLogin : undefined,
      isSuperuser: principal?.engine === 'pgsql' ? principal.pgsqlDetails?.isSuperuser : undefined,
      createDb: principal?.engine === 'pgsql' ? principal.pgsqlDetails?.createDb : undefined,
      createRole: principal?.engine === 'pgsql' ? principal.pgsqlDetails?.createRole : undefined,
      inherit: principal?.engine === 'pgsql' ? principal.pgsqlDetails?.inherit : undefined,
      connectionLimit: principal?.engine === 'pgsql' ? principal.pgsqlDetails?.connectionLimit : undefined,
      validUntil: principal?.engine === 'pgsql' ? principal.pgsqlDetails?.validUntil : undefined,
    } as any,
  });

  // PostgreSQL 19+ CREATE ROLE can be server-authored via pg_get_role_ddl; the
  // handler stamps a header comment onto that path. Report which path was used.
  if (principal?.engine === 'pgsql' && operation === 'create') {
    const native = typeof result.script === 'string' && result.script.includes('pg_get_role_ddl');
    window.electronAPI?.analytics
      ?.track('feature:script_native_ddl', { engine: 'pgsql', target: 'role', native })
      .catch(() => {
        // analytics is fire-and-forget
      });
  }

  return result;
}

async function previewOracleUserScript(options: {
  api: SecurityApi;
  context: SecurityExplorerNodeContext;
  operation: ScriptOperation;
  notes: string[];
}) {
  const { api, context, operation, notes } = options;
  const loginsResult = await api.fetchLogins({ connectionId: context.connectionId });
  const principal = loginsResult.success ? (loginsResult.logins ?? []).find((l) => l.name === context.principalName) : undefined;

  const password = operation === 'create' ? 'REPLACE_WITH_STRONG_PASSWORD' : undefined;
  if (password) notes.push('Passwords are not retrievable; replace REPLACE_WITH_STRONG_PASSWORD before executing.');

  return api.previewScript({
    connectionId: context.connectionId,
    operation,
    target: 'login',
    params: {
      name: context.principalName,
      password,
      defaultTablespace: principal?.oracleDetails?.defaultTablespace,
      temporaryTablespace: principal?.oracleDetails?.temporaryTablespace,
      isDisabled: principal?.oracleDetails?.accountStatus === 'LOCKED' || principal?.oracleDetails?.accountStatus === 'EXPIRED & LOCKED' || undefined,
    } as any,
  });
}

export async function previewSecurityScriptForExplorerNode(options: {
  api: SecurityApi;
  context: SecurityExplorerNodeContext;
  operation: ScriptOperation;
  notes: string[];
}) {
  const { api, context, operation, notes } = options;

  if (context.nodeType === 'login' || context.nodeType === 'login-role') {
    return previewLoginScript({ api, context, operation, notes });
  }

  if (context.nodeType === 'oracle-user') {
    return previewOracleUserScript({ api, context, operation, notes });
  }

  if (context.nodeType === 'oracle-role') {
    return previewRoleScript({ api, context, operation });
  }

  if (context.nodeType === 'database-user') {
    return previewUserScript({ api, context, operation });
  }

  if (context.nodeType === 'schema') {
    return previewSchemaScript({ api, context, operation });
  }

  return previewRoleScript({ api, context, operation });
}
